import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../context/AuthContext';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../../components/ui/dialog';
import { investmentApi } from '../../../services/api';
import { toast } from 'sonner';
import { Landmark, Plus, RefreshCw, ChevronRight } from 'lucide-react';

const STATUS_COLORS = {
  DRAFT: 'bg-stone-100 text-stone-600',
  ACTIVE: 'bg-emerald-100 text-emerald-700',
  CLOSED: 'bg-gray-200 text-gray-600',
};

const EMPTY = { name: '', approval_year: new Date().getFullYear(), controlling_area: '1000', currency: 'USD', total_budget: 0, description: '' };

export default function InvestmentPrograms() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await investmentApi.listPrograms();
      setPrograms(res.data || []);
    } catch (e) {
      toast.error('Failed to load investment programs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      setSaving(true);
      const res = await investmentApi.createProgram({
        ...form,
        approval_year: parseInt(form.approval_year),
        total_budget: parseFloat(form.total_budget) || 0,
      });
      toast.success(`Program ${res.data.program_id} created`);
      setOpen(false);
      setForm(EMPTY);
      navigate(`/ps/investment-programs/${res.data.program_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const money = (v, cur) => `${cur || ''} ${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`.trim();

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="investment-programs-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Landmark className="w-7 h-7 text-orange-600" />
              Investment Programs
            </h1>
            <p className="text-gray-600 mt-1">
              Plan and roll up capital-investment budget across a hierarchy of positions, with WBS assignments.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} data-testid="ip-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
            {canWrite('investment_program') && (<Button onClick={() => setOpen(true)} data-testid="ip-new-btn"><Plus className="w-4 h-4 mr-1" />New Program</Button>)}
          </div>
        </div>

        <Card>
          <CardHeader><CardTitle>{programs.length} Program(s)</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading…</div>
            ) : programs.length === 0 ? (
              <div className="text-center py-10 text-gray-500">No investment programs yet. Click <strong>New Program</strong>.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Program ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Approval Year</TableHead>
                    <TableHead>Controlling Area</TableHead>
                    <TableHead className="text-right">Total Budget</TableHead>
                    <TableHead>Positions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {programs.map((p) => (
                    <TableRow
                      key={p.program_id}
                      className="cursor-pointer hover:bg-stone-50"
                      onClick={() => navigate(`/ps/investment-programs/${p.program_id}`)}
                      data-testid={`ip-row-${p.program_id}`}
                    >
                      <TableCell className="font-mono text-sm">{p.program_id}</TableCell>
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell>{p.approval_year}</TableCell>
                      <TableCell>{p.controlling_area}</TableCell>
                      <TableCell className="text-right">{money(p.total_budget, p.currency)}</TableCell>
                      <TableCell>{p.position_count}</TableCell>
                      <TableCell><Badge className={STATUS_COLORS[p.status]}>{p.status}</Badge></TableCell>
                      <TableCell><ChevronRight className="w-4 h-4 text-gray-400" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent data-testid="ip-create-dialog">
            <DialogHeader><DialogTitle>New Investment Program</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ip-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Approval Year</Label>
                  <Input type="number" value={form.approval_year} onChange={(e) => setForm({ ...form, approval_year: e.target.value })} data-testid="ip-year" />
                </div>
                <div>
                  <Label>Controlling Area</Label>
                  <Input value={form.controlling_area} onChange={(e) => setForm({ ...form, controlling_area: e.target.value })} data-testid="ip-ca" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Currency</Label>
                  <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="ip-currency" />
                </div>
                <div>
                  <Label>Total Budget</Label>
                  <Input type="number" value={form.total_budget} onChange={(e) => setForm({ ...form, total_budget: e.target.value })} data-testid="ip-budget" />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="ip-desc" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} data-testid="ip-create-submit">{saving ? 'Creating…' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
